import { SpinnerIcon } from '@acceso/design-system';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { InfiniteData } from '@tanstack/react-query';
import clsx from 'clsx';
import React, { Suspense, useEffect } from 'react';
import { Group, Panel } from 'react-resizable-panels';

import { BroadcastScheduleProgramBodySegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/BroadcastScheduleProgramBodySegmentToolkit';
import { ESegmentToolkit } from '@/features/broadcast-schedule-program/components/BroadcastScheduleProgramBody/components/BroadcastScheduleProgramBodySegmentToolkit/types';
import { useBroadcastScheduleProgram } from '@/features/broadcast-schedule-program/context/BroadcastScheduleProgramContext';
import { useNavigationList } from '@/features/broadcast-schedule-program/hooks/useNavigationList';

import { useClipPermissions } from '../../hooks/useClipPermissions';
import { TClip } from '../../types';
import { ClipDetailCard } from '../ClipDetailCard/ClipDetailCard';

import { ClipBodyCard } from './ClipBodyCard';

type TClipBodyProps = {
  clipsData: UseInfiniteQueryResult<InfiniteData<TClip[], unknown>, Error>;
};

export function ClipBody({ clipsData }: TClipBodyProps) {
  const {
    visibleBroadcastProgramCards,
    pinnedId,
    toolkitClipComponents,
    toolkitCardRegionRef,
    clipItems,
    activeClip,
    isHeaderVisible,
    setActiveClip,
  } = useBroadcastScheduleProgram();

  const { canUpdate: canUpdateClips } = useClipPermissions();

  const { handleGoToNext, handleGoToPrevious, handleGoToFirst, handleGoToLast } = useNavigationList(
    {
      items: clipItems,
      activeItem: activeClip,
      setActiveItem: setActiveClip,
    }
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.altKey && event.key === 'ArrowDown') {
        event.preventDefault();
        handleGoToNext();
      }
      if (event.altKey && event.key === 'ArrowUp') {
        event.preventDefault();
        handleGoToPrevious();
      }
      if (event.altKey && event.key === 'Home') {
        event.preventDefault();
        handleGoToFirst();
      }
      if (event.altKey && event.key === 'End') {
        event.preventDefault();
        handleGoToLast();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleGoToNext, handleGoToPrevious, handleGoToFirst, handleGoToLast]);

  return (
    <Group
      data-id="clip-body-panels"
      className={clsx(
        'flex flex-col gap-2 lg:flex-row',
        isHeaderVisible ? 'lg:max-h-[calc(100vh-150px)]' : 'lg:max-h-[calc(100vh-75px)]'
      )}
    >
      {visibleBroadcastProgramCards?.segments && (
        <Panel minSize={300} maxSize="25%">
          <ClipBodyCard clipsData={clipsData} />
        </Panel>
      )}
      {visibleBroadcastProgramCards?.segmentDetail && (
        <>
          <Panel minSize="35%">
            <ClipDetailCard />
          </Panel>
        </>
      )}
      {pinnedId &&
        toolkitClipComponents[pinnedId] &&
        (pinnedId !== ESegmentToolkit.CORTES || canUpdateClips) && (
          <Suspense fallback={<SpinnerIcon className="animation infinite" />}>
            <Panel minSize={300}>
              <div
                ref={toolkitCardRegionRef}
                className="order-3 w-full !min-w-full flex-auto overflow-auto lg:order-2"
              >
                {React.createElement(toolkitClipComponents[pinnedId], { clipItems })}
              </div>
            </Panel>
          </Suspense>
        )}
      {visibleBroadcastProgramCards?.segmentToolkit && (
        <BroadcastScheduleProgramBodySegmentToolkit clips />
      )}
    </Group>
  );
}
